import React from "react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart3, FileText, Vote, Globe, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CountryFlag from "@/components/country/CountryFlag";
import { cn } from "@/lib/utils";

interface ResourceNavigationTabsProps {
  countryCode?: string;
  countryName?: string;
  showGlobalOption?: boolean;
  showCountrySelector?: boolean;
  availableCountries?: Array<{ code: string; name: string; active: boolean }>;
  onCountryChange?: (countryCode: string) => void;
}

const ResourceNavigationTabs = ({
  countryCode,
  countryName,
  showGlobalOption = true,
  showCountrySelector = false,
  availableCountries = [],
  onCountryChange,
}: ResourceNavigationTabsProps) => {
  const location = useLocation();

  const getNavigationItems = () => {
    const baseItems = [
      {
        label: "Home",
        href: "/",
        icon: BarChart3,
        isActive: location.pathname === "/",
      },
    ];

    // Add Country item only if we have a countryCode
    if (countryCode) {
      baseItems.push({
        label: "Country",
        href: `/cards/${countryCode}`,
        icon: Globe,
        isActive: location.pathname === `/cards/${countryCode}`,
      });
    }

    baseItems.push(
      {
        label: "Insights",
        href: countryCode ? `/insights/${countryCode}` : "/insights",
        icon: FileText,
        isActive: location.pathname.includes("/insights"),
      },
      {
        label: "Polls",
        href: countryCode ? `/polls/${countryCode}` : "/polls",
        icon: Vote,
        isActive: location.pathname.includes("/polls"),
      }
    );

    if (showGlobalOption) {
      baseItems.push({
        label: "All Countries",
        href: "/countries",
        icon: Globe,
        isActive: location.pathname === "/countries",
      });
    }

    return baseItems;
  };

  const navigationItems = getNavigationItems();

  return (
    <Card className="mb-6">
      <CardContent className="p-3 md:p-4">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
          {/* Navigation tabs with horizontal scroll on mobile */}
          <div className="flex items-center gap-1 overflow-x-auto scrollbar-hide w-full sm:w-auto">
            <div className="flex items-center gap-1 min-w-max">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.href}
                    asChild
                    // always use ghost variant, override styles for active state
                    variant="ghost"
                    size="sm"
                    className={cn(
                      "gap-2 whitespace-nowrap flex-shrink-0",
                      item.isActive && [
                        "bg-accent font-medium", // accent bg
                        "!text-gray-900", // dark text in light mode
                        "dark:!text-gray-100", // light text in dark mode
                        "!hover:text-gray-900", // maintain text on hover
                        "dark:!hover:text-gray-100",
                      ],
                      !item.isActive && [
                        "text-gray-900 dark:text-gray-100", // default text for inactive
                      ]
                    )}
                  >
                    <Link to={item.href}>
                      <Icon className="h-4 w-4" />
                      <span className="hidden md:inline">{item.label}</span>
                    </Link>
                  </Button>
                );
              })}
            </div>
          </div>

          {/* Right side content */}
          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            {showCountrySelector && availableCountries.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-2 min-w-0 flex-shrink-0">
                    <Globe className="h-4 w-4 flex-shrink-0" />
                    <span className="truncate max-w-[120px] sm:max-w-none">
                      {countryName || "Select Country"}
                    </span>
                    <ChevronDown className="h-4 w-4 flex-shrink-0" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {availableCountries
                    .filter((country) => country.active)
                    .map((country) => (
                      <DropdownMenuItem
                        key={country.code}
                        onClick={() => onCountryChange?.(country.code)}
                        className={cn(
                          "relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors",
                          "text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100 focus:bg-gray-100 dark:focus:bg-gray-700 focus:text-gray-900 dark:focus:text-gray-100",
                          "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
                          countryCode === country.code && [
                            "bg-primary font-medium",
                            "!text-gray-900",
                            "dark:!text-gray-100",
                            "!hover:text-gray-900",
                            "dark:!hover:text-gray-100",
                          ]
                        )}
                      >
                        <div className="flex items-center gap-2">
                          <CountryFlag
                            countryCode={country.code}
                            className="w-5 h-3 flex-shrink-0"
                          />
                          <span className="truncate">{country.name}</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {!showCountrySelector && countryCode && (
              <div className="text-sm text-muted-foreground hidden md:block truncate">
                Viewing: {countryName || countryCode.toUpperCase()}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResourceNavigationTabs;
